import { useEffect, useId, useRef } from 'react';
import { classroomMarkerLabel, type ClassroomMarkerMode } from '../domain/classroom-marker-display';
import { ClassroomMarkerOptions } from './ClassroomStudioControls';

export function HomeworkMarkerControl({
  open,
  onOpenChange,
  markerMode,
  customMarkerCount,
  onMarkerMode,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly markerMode: ClassroomMarkerMode;
  readonly customMarkerCount: number;
  readonly onMarkerMode: (mode: ClassroomMarkerMode, count?: number) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target))
        onOpenChange(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onOpenChange(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [onOpenChange, open]);
  return (
    <div
      className="grading-marker-control"
      ref={rootRef}
      onBlur={(event) => {
        if (
          event.relatedTarget instanceof Node &&
          !event.currentTarget.contains(event.relatedTarget)
        )
          onOpenChange(false);
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        aria-label="手数显示"
        title={`手数显示：${classroomMarkerLabel(markerMode, customMarkerCount)}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => onOpenChange(!open)}
      >
        <span className="grading-number-icon" aria-hidden="true">
          12
        </span>
        手数
      </button>
      {open ? (
        <div
          className="grading-marker-popover"
          id={panelId}
          role="dialog"
          aria-label="手数显示设置"
        >
          <ClassroomMarkerOptions
            markerMode={markerMode}
            customMarkerCount={customMarkerCount}
            onSelect={(mode, count) => {
              onMarkerMode(mode, count);
              onOpenChange(false);
              triggerRef.current?.focus();
            }}
            onCustomChange={(count) => onMarkerMode('CUSTOM', count)}
          />
        </div>
      ) : null}
    </div>
  );
}
